import React from 'react';
import { AlertTriangle, CheckCircle2, Clock3, Info } from 'lucide-react';
import { formatIst } from '../format';
import { namedFaults, stateOf } from '../status';

function evidenceOf(item) {
  return (
    item.evidence ||
    item.trend_evidence ||
    item.explanation ||
    'Telemetry changed from this pump’s usual operating range.'
  );
}

export function RootCauseDiagnostics({ payload }) {
  if (!payload) return null;
  const state = stateOf(payload);
  const faults = namedFaults(payload);
  const warnings = payload.early_warnings || [];
  const active = state === 'ANOMALY';
  const hasSignal = active || warnings.length > 0;
  const evidence = active
    ? faults.map((fault) => fault.evidence || 'The backend returned this candidate issue.')
    : warnings.map(evidenceOf);

  return (
    <section className="card-panel diagnostics-panel" aria-labelledby="diagnostics-title">
      <div className="panel-heading">
        <div>
          <span className="panel-kicker">Decision support</span>
          <h2 id="diagnostics-title">Root Cause Diagnostics</h2>
        </div>
        {hasSignal && (
          <span className={`diagnostic-badge ${active ? 'is-red' : 'is-amber'}`}>
            {active ? 'Fault diagnosed' : 'Decision support'}
          </span>
        )}
      </div>

      {!hasSignal ? (
        <div className="diagnostic-empty">
          <CheckCircle2 size={20} />
          <div>
            <strong>No active root cause detected</strong>
            <span>No current fault evidence was returned.</span>
          </div>
        </div>
      ) : (
        <div className={`diagnostic-summary ${active ? 'is-red' : 'is-amber'}`}>
          <AlertTriangle size={20} />
          <div>
            <strong>
              {active
                ? faults.length
                  ? 'Active candidate cause identified'
                  : 'Candidate issue detected'
                : 'Early change needs review'}
            </strong>
            <ul>
              {evidence.slice(0, 4).map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <p className="diagnostic-note">
        <Info size={14} />
        Decision support only; not a confirmed physical diagnosis.
      </p>
    </section>
  );
}

export function RecentAlerts({ payload }) {
  if (!payload) return null;
  const alerts = payload.alert_history || [];

  return (
    <section className="card-panel alerts-panel" aria-labelledby="alerts-title">
      <div className="panel-heading">
        <div>
          <span className="panel-kicker">Event history</span>
          <h2 id="alerts-title">Recent Alerts</h2>
        </div>
        <span className="panel-meta">Latest response</span>
      </div>

      {alerts.length === 0 ? (
        <div className="alerts-empty">
          <Clock3 size={20} />
          <div>
            <strong>No recent alerts in this dashboard response.</strong>
            <span>Current compact API response does not include alert history.</span>
          </div>
        </div>
      ) : (
        <div className="alerts-list">
          <div className="alert-row alert-header">
            <span>Time</span>
            <span>Alert</span>
            <span>Status</span>
          </div>
          {alerts.slice(0, 5).map((alert, index) => {
            const ongoing = alert.end_time === 'ONGOING';
            return (
              <div className="alert-row" key={`${alert.start_time}-${index}`}>
                <span>{formatIst(alert.start_time)}</span>
                <strong>{alert.reason || 'Pump anomaly'}</strong>
                <span className={`status-pill ${ongoing ? 'is-red' : 'is-grey'}`}>
                  {ongoing ? 'Active' : 'Resolved'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default RootCauseDiagnostics;
